import { useEffect, useState, useRef, useContext } from "react"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Tag } from "primereact/tag"
import { Paginator } from "primereact/paginator"
import { Tooltip } from "primereact/tooltip"
import CustomTable from "../CustomTable/CustomTable"
import { formatToLocalDateTime } from "../../helpers/dateConversion"
import { Toast } from "primereact/toast"
import { UserContext } from "../../context/userContext"
import { GET_USERS_FROM_LIST } from "./taskQueries"
import { useQuery } from "@apollo/client"
import LoaderFullPage from "../Loaders"
import {
  FilterLocation,
  FilterDefaults,
  FilterTypes,
} from "../CustomFilter/FilterConstants"
import { DateTime } from "luxon"
import { useNavigate } from "react-router-dom"

//Tasks modules
import { taskApiGetAllAssigned, taskApiMarkCompletePUT } from "./TasksApi"
import { DueStatus, DueStatusOptions, TypeFilter } from "./Utils"
import { Accordion, AccordionTab } from "primereact/accordion"
import { ReactComponent as NotesIcon } from "../../assets/icons/notes_icon.svg"
import { classNames } from "primereact/utils"
import { LogError } from "../../helpers/logger"
import CustomFilter from "../CustomFilter/CustomFilter"

//Styles
import styles from "../../styles/Tasks.module.css"
import noteStyle from "../../styles/NotificationsForm.module.css"
import StandardPageHeader from "../StandardPageHeader/StandardPageHeader"
import { handleScheduledAssessment } from "../notifications/actionUtils"

const TasksDialog = ({ visible, onHide, organizations }) => {
  const navigate = useNavigate()
  const defaultPageSize = 10
  const defaultPageNum = 1

  const userCtx = useContext(UserContext)
  const toast = useRef(null)

  const [loading, setLoading] = useState(false)
  const [organizationOptions, setOrganizationOptions] = useState([])
  const [taskUsers, setTaskUsers] = useState([])
  const [refreshTasks, setRefreshTasks] = useState(new Date().getTime())
  const [refreshUsers, setRefreshUsers] = useState(false)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [tasks, setTasks] = useState({
    items: [],
    numResults: 0,
    numTotal: 0,
    pageNum: defaultPageNum,
    pageSize: defaultPageSize,
  })
  const [lastRefresh, setLastRefresh] = useState(new Date().getTime())
  const [taskFilters, setTaskFilters] = useState([])
  const [taskSorting, setTaskSorting] = useState([])

  const { refetch } = useQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    variables: {
      uid: userCtx.participant_id,
    },
    onError: (e) => {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          "Unable to retrieve user information, please refer to the console for more information",
      })
      setRefreshUsers(false)
      setLoading(false)
    },
    onCompleted: (data) => {
      if (data.users !== null) {
        let newUsers = data.users
          .filter((x) => x !== null)
          .map((x) => {
            return {
              id: x.staff_id,
              uid: x.staff_id,
              pid: undefined,
              alias: x.staff_alias,
              status: x.staff_user_status,
              email: x.staff_email_address,
              displayName: `${x.staff_name_first} ${x.staff_name_last}`,
            }
          })

        setTaskUsers([...taskUsers, ...newUsers])
      }
      setRefreshUsers(false)
      setLoading(false)
    },
  })

  useEffect(() => {
    if (refreshUsers) {
      refetch()
    }
  }, [refreshUsers, refetch])

  useEffect(() => {
    if (visible) {
      getAssignedTasks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    if (refreshTasks > lastRefresh) {
      getAssignedTasks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTasks])

  useEffect(() => {
    if (organizations) {
      setOrganizationOptions(
        organizations?.map((org) => {
          return {
            name: org.organization_name,
            code: org.organization_id,
          }
        }),
      )
    }
  }, [organizations])

  useEffect(() => {
    // Set filter options
    setTaskFilters([
      // Bar filters
      {
        key: "organization_filter",
        label: "Organization",
        defaultValue: FilterDefaults.multiselect,
        currentValue: FilterDefaults.multiselect,
        type: FilterTypes.multiselect,
        location: FilterLocation.bar,
        options: organizationOptions,
        placeholder: "Select organization",
        showClear: false,
        showFilter: false,
      },
      // Panel filters
      {
        key: "text_search",
        label: "Task title or notes",
        placeholder: "Search",
        defaultValue: FilterDefaults.text,
        currentValue: FilterDefaults.text,
        type: FilterTypes.text,
        location: FilterLocation.panel,
      },
      {
        key: "status_filter",
        label: "Due status",
        defaultValue: FilterDefaults.multiselect,
        currentValue: FilterDefaults.multiselect,
        type: FilterTypes.multiselect,
        location: FilterLocation.panel,
        options: DueStatusOptions,
        placeholder: "Select",
        showClear: false,
        showFilter: false,
      },
      {
        key: "created_date_filter",
        label: "Created date",
        defaultValue: FilterDefaults.calendar,
        currentValue: FilterDefaults.calendar,
        type: FilterTypes.calendar,
        location: FilterLocation.panel,
        placeholder: "Select",
        showTime: false,
        showIcon: false,
        rangeSelect: true,
      },
      {
        key: "due_date_filter",
        label: "Due date",
        defaultValue: FilterDefaults.calendar,
        currentValue: FilterDefaults.calendar,
        type: FilterTypes.calendar,
        location: FilterLocation.panel,
        placeholder: "Select",
        showTime: false,
        showIcon: false,
        rangeSelect: true,
      },
      {
        key: "include_active_tasks",
        label: "Include active tasks",
        defaultValue: true,
        currentValue: true,
        type: FilterTypes.checkbox,
        location: FilterLocation.panel,
      },
      {
        key: "include_completed_tasks",
        label: "Include completed tasks",
        defaultValue: false,
        currentValue: false,
        type: FilterTypes.checkbox,
        location: FilterLocation.panel,
      },
    ])

    // Set sort options
    setTaskSorting([
      { name: "Created", code: "CreatedAt", direction: "ASC", enabled: false },
      { name: "Due on", code: "DueOn", direction: "ASC", enabled: true },
    ])
  }, [organizationOptions])

  async function markTaskAsDone(taskId) {
    setLoading(true)
    try {
      await taskApiMarkCompletePUT(userCtx.tokens.getTaskToken(), taskId)
      setRefreshTasks(new Date().getTime())
      userCtx.setPendingTasksCount(userCtx.pendingTasksCount - 1)
    } catch (error) {
      LogError(`Unable to mark task as complete (id: ${taskId})`, error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Unable to mark task as complete (id: ${taskId}). See console for details.`,
      })
    }
    setLoading(false)
  }

  function checkUserCache(uids) {
    if (!!taskUsers) {
      const missingUsers = uids.filter(
        (uid) => taskUsers.findIndex((y) => y.uid === uid) < 0,
      )
      if (missingUsers.length > 0) {
        setLoading(true)
        refetch({ list: missingUsers })
      }
    } else {
      refetch(uids)
    }
  }

  const loadUsers = (x) => {
    checkUserCache(x)
  }

  function getFilterByKey(key) {
    const filter = taskFilters.find((f) => f.key === key)
    return filter
  }

  async function getAssignedTasks(_page = 1, _pageSize) {
    // Limit refreshes
    if (new Date().getTime() - lastRefresh < 1000) return

    try {
      setLoading(true)
      setLastRefresh(new Date().getTime())

      const organization_filter = getFilterByKey("organization_filter")
      const created_date_filter = getFilterByKey("created_date_filter")
      const due_date_filter = getFilterByKey("due_date_filter")
      const include_active_tasks = getFilterByKey("include_active_tasks")
      const include_completed_tasks = getFilterByKey("include_completed_tasks")
      const status_filter = getFilterByKey("status_filter")

      // Get today's start and end
      const todayStart = DateTime.local().startOf("day")
      const todayEnd = DateTime.local().endOf("day")

      const appliedFilters = {
        groupIds:
          organization_filter.currentValue.length > 0
            ? organization_filter.currentValue
            : ["All"],
        userId: userCtx.participant_id,
        textSearch: getFilterByKey("text_search").currentValue,
        createdDateStart: created_date_filter.currentValue
          ? created_date_filter.currentValue[0]
          : null,
        createdDateEnd: created_date_filter.currentValue
          ? created_date_filter.currentValue[1]
          : null,
        dueDateStart: due_date_filter.currentValue
          ? due_date_filter.currentValue[0]
          : null,
        dueDateEnd: due_date_filter.currentValue
          ? due_date_filter.currentValue[1]
          : null,
        userDateStart: todayStart,
        userDateEnd: todayEnd,
        includeActive:
          !include_active_tasks.currentValue &&
          !include_completed_tasks.currentValue
            ? true
            : include_active_tasks.currentValue,
        includeCompleted: include_completed_tasks.currentValue,
        dueStatus:
          status_filter.currentValue.length > 0
            ? status_filter.currentValue
            : ["pending", "due", "overdue"],
        pageNum: _page,
        pageSize: _pageSize || pageSize,
        sorting: taskSorting
          .filter((s) => s.enabled)
          .map((s) => {
            return {
              sortColumn: s.code,
              sortDirection: s.direction,
            }
          }),
      }

      const res = await taskApiGetAllAssigned(
        userCtx.tokens.getTaskToken(),
        appliedFilters,
      )

      if (res) {
        setTasks(res)

        // Fetch user data (for task creator)
        const allUserIds = [...res.items.map((x) => x.createdByUserId)]
        const uniqueUserIds = [...new Set(allUserIds)]

        if (uniqueUserIds.length > 0) {
          loadUsers(uniqueUserIds)
        }
      }
    } catch (error) {
      LogError(`Unable to list assigned tasks`, error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to list tasks.",
      })
    }
    setLoading(false)
  }

  function getUser(id) {
    if (taskUsers) {
      const usr = taskUsers?.filter((x) => x.uid === id)

      if (usr.length > 0) {
        return usr[0]
      } else {
        return null
      }
    } else {
      return null
    }
  }

  //Convert to SOAR context
  function GetUserById(id) {
    let usr = { displayName: "System" }

    if (!taskUsers || taskUsers.length === 0) {
      usr.displayName = "Loading..."
    } else if (id !== "0") {
      usr = getUser(id)
    }

    return usr ? usr.displayName : "Unknown User"
  }

  function handleTaskAction(action, task) {
    switch (action) {
      case "#ScheduledAssessment":
        onHide() // Close tasks dialog
        handleScheduledAssessment(task, navigate)
        break

      default:
        navigate(action)
    }
  }

  const getOrganization = (organization_id) => {
    if (organization_id !== "0" && organizations) {
      let org = organizations.filter(
        (x) => x.organization_id === organization_id,
      )

      if (org.length > 0) {
        return org[0].organization_name
      } else {
        org = organizations.filter(
          (x) => x.organization_alias === organization_id,
        )
        if (org.length > 0) {
          return org[0].organization_name
        } else {
          return "Unknown organization"
        }
      }
    } else {
      return ""
    }
  }

  const renderFirstCol = (task) => {
    return (
      <div>
        <span style={{ fontWeight: "700", display: "block" }}>
          {task.title}
        </span>
        <span
          style={{ display: "block", marginTop: "0.2rem" }}
        >{`By ${task.createdBy}`}</span>
        {task?.systemManaged && (
          <Tag
            value="system-managed"
            className={noteStyle.systemNot}
            severity="info"
            icon="pi pi-lock"
            style={{
              fontSize: "12px",
              fontWeight: "600",
              width: "max-content",
            }}
          />
        )}
      </div>
    )
  }

  const renderDueCompletedCol = (task) => {
    return (
      <div>
        <span
          style={{ fontWeight: "600", display: "block", marginRight: "8px" }}
        >
          <span style={{ marginRight: "8px" }}>
            {!task.completedOn ? "Due on" : "Completed on"}
          </span>
          {task.completedOn && (
            <>
              <i
                className={
                  styles.due_date_help_icon +
                  " due_date_help_icon pi pi-info p-text-secondary p-overlay-badge"
                }
                data-pr-tooltip={`Due on: ${formatToLocalDateTime(task.dueOn)}`}
                data-pr-position="right"
              />
              <Tooltip target=".due_date_help_icon" />
            </>
          )}
        </span>
        <span style={{ display: "block", marginTop: "0.2rem" }}>
          <span>
            {!task.completedOn
              ? formatToLocalDateTime(task.dueOn)
              : formatToLocalDateTime(task.completedOn)}
          </span>
        </span>
        {task.dueStatus === DueStatus.Overdue && task.completedOn === null && (
          <Tag
            value="Overdue"
            severity="error"
            icon="pi pi-calendar-times"
            className={styles.past_due_tag}
            style={{ fontSize: "12px", marginTop: "0.3rem", fontWeight: "600" }}
          />
        )}
        {task.completedOn !== null && (
          <>
            <Tag
              value="Completed"
              severity="success"
              icon="pi pi-check"
              className={styles.past_due_tag}
              style={{
                fontSize: "12px",
                marginTop: "0.3rem",
                fontWeight: "600",
              }}
            />
          </>
        )}
      </div>
    )
  }

  const renderDescription = (task) => {
    if (task.description && task.description !== "") {
      const header = (
        <div className="flex gap-2 align-items-center">
          {<NotesIcon />}{" "}
          <span className="font-medium text-sm line-height-1">Note</span>
        </div>
      )

      return (
        <Accordion className={styles.task_description}>
          <AccordionTab headerTemplate={header}>
            <div dangerouslySetInnerHTML={{ __html: task.description }} />
          </AccordionTab>
        </Accordion>
      )
    }
  }

  const headersTasks = () => {
    return [
      {
        custom: true,
        template: renderFirstCol,
        size: 3,
      },
      {
        value: "organization",
        label: "Organization",
        size: 3,
      },
      {
        value: "createdAt",
        label: "Created",
        size: 2,
      },
      {
        custom: true,
        template: renderDueCompletedCol,
        size: 2,
      },
      {
        value: "actions",
        label: "",
        action: true,
      },
      {
        value: renderDescription,
        label: "",
        footer: true,
        size: 12,
      },
    ]
  }

  const getTaskActions = (task) => {
    if (task.primaryActionLabel || task.secondaryActionLabel) {
      return (
        <>
          {task.primaryActionLabel && (
            <Button
              label={task.primaryActionLabel}
              className={classNames(
                "p-button-outlined",
                styles.btn_task_action,
              )}
              icon="pi pi-external-link"
              iconPos="right"
              onClick={() => handleTaskAction(task.primaryActionUrl, task)}
            />
          )}
          {task.secondaryActionLabel && (
            <Button
              label={task.secondaryActionLabel}
              className={classNames(
                "p-button-outlined",
                styles.btn_task_action,
              )}
              icon="pi pi-external-link"
              iconPos="right"
              onClick={() => handleTaskAction(task.secondaryActionUrl, task)}
            />
          )}
        </>
      )
    } else if (task.completedOn === null) {
      return (
        <Button
          label="Mark complete"
          className={classNames("p-button-primary", styles.btn_mark_complete)}
          icon="pi pi-check"
          iconPos="left"
          onClick={() => markTaskAsDone(task.id)}
        />
      )
    }
  }

  const mapTasks = () => {
    return tasks?.items?.map((task) => {
      return {
        title: task.title,
        createdBy: GetUserById(task.createdByUserId),
        type: task.systemManaged ? TypeFilter.System : TypeFilter.User,
        organization: getOrganization(task.groupId),
        createdAt: formatToLocalDateTime(task.createdAt),
        dueStatus: CalcDueStatus(task),
        dueOn: task.dueOn,
        completedOn: task.completedOn,
        actions: getTaskActions(task),
        description: task.description,
        systemManaged: task.systemManaged,
      }
    })
  }

  function CalcDueStatus(task) {
    // Get today's start and end
    const todayStart = DateTime.local().startOf("day")
    const todayEnd = DateTime.local().endOf("day")
    const dueOn = DateTime.fromISO(task.dueOn, { zone: "utc" }).toLocal()
    let dueStatus = "Unknown"

    if (dueOn > todayEnd) {
      dueStatus = DueStatus.Pending
    } else if (dueOn < todayStart) {
      dueStatus = DueStatus.Overdue
    } else if (dueOn >= todayStart && dueOn <= todayEnd) {
      dueStatus = DueStatus.Due
    } else {
      LogError(
        "Unable to determine task due date. DueStatus set to 'Unknown'",
        `task.id: ${task.id}, task.dueOn: ${dueOn}`,
      )
    }

    return dueStatus
  }

  const onPageChange = (event) => {
    setPageSize(event.rows)
    getAssignedTasks(event.page + 1, event.rows)
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={
        <StandardPageHeader
          title="Tasks"
          refreshData={() => getAssignedTasks()}
          overrideClassNames={"pageHeaderCustom"}
        />
      }
      style={{
        width: "96vw",
        height: "96vh",
        maxWidth: "1400px",
      }}
      className="custom_dialog"
    >
      <Toast ref={toast} />
      <CustomFilter
        filters={taskFilters}
        setFilters={setTaskFilters}
        sorting={taskSorting}
        setSorting={setTaskSorting}
        applyFilters={() => setRefreshTasks(new Date().getTime())}
      />
      {loading ? (
        <LoaderFullPage />
      ) : (
        <>
          <CustomTable
            headers={headersTasks()}
            collection={mapTasks()}
            tableRowStyles={{ alignItems: "start" }}
            actionStyles={{ maxWidth: "none" }}
            styles={{ marginTop: "0.6rem", marginBottom: "0.6rem" }}
            maxRows={defaultPageSize}
            disablePagination
          />
          <Paginator
            first={tasks.pageSize * (tasks.pageNum - 1)}
            rows={tasks.pageSize}
            totalRecords={tasks.numTotal}
            onPageChange={onPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Dialog>
  )
}

export default TasksDialog
